import { Button } from '@/components/ui/button';
import { OverlayPanel } from '@/components/ui/overlay-panel';
import { useActivityDetails } from '@/hooks/queries/use-activity-details';
import type { ActivityInterval, ActivityScope } from '@/lib/api/user-activity';
import type {
  FeatureDetail,
  TaskDetail,
  DecisionDetail,
  SessionDetail,
  EpicRequestDetail,
  EpicDetail,
  ImplementationTimeDetail,
} from '@/lib/api/user-activity-details';
import {
  FileQuestion,
  CheckCircle2,
  Lightbulb,
  Bot,
  ScrollText,
  Layers,
  Clock,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  FeatureDetailList,
  TaskDetailList,
  DecisionDetailList,
  SessionDetailList,
  EpicRequestDetailList,
  EpicDetailList,
  ImplementationTimeDetailList,
} from './detail-lists';

import type { MetricType } from '@/pages/dashboard';

interface MetricDetailSheetProps {
  metricType: MetricType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interval: ActivityInterval;
  page: number;
  scope: ActivityScope;
  scopeId?: string;
  timeZone: string;
  startDate?: string;
  endDate?: string;
}

const METRIC_TITLES: Record<MetricType, string> = {
  features: 'Features Created',
  tasks: 'Tasks Completed',
  decisions: 'Decisions Logged',
  sessions: 'AI Sessions',
  epicRequests: 'Epic Requests Created',
  epics: 'Epics Created',
  implementationTime: 'Elapsed Time',
} as const;

const METRIC_COLORS: Record<MetricType, string> = {
  features: 'text-blue-400',
  tasks: 'text-emerald-400',
  decisions: 'text-amber-400',
  sessions: 'text-purple-400',
  epicRequests: 'text-cyan-400',
  epics: 'text-indigo-400',
  implementationTime: 'text-rose-400',
} as const;

const EMPTY_MESSAGES: Record<MetricType, { message: string; icon: React.ComponentType<{ className?: string }> }> = {
  features: {
    message: 'No features were created during this period',
    icon: FileQuestion,
  },
  tasks: {
    message: 'No tasks were completed during this period',
    icon: CheckCircle2,
  },
  decisions: {
    message: 'No decisions were logged during this period',
    icon: Lightbulb,
  },
  sessions: {
    message: 'No AI sessions were recorded during this period',
    icon: Bot,
  },
  epicRequests: {
    message: 'No epic requests were created during this period',
    icon: ScrollText,
  },
  epics: {
    message: 'No epics were created during this period',
    icon: Layers,
  },
  implementationTime: {
    message: 'No elapsed time was recorded during this period',
    icon: Clock,
  },
} as const;

function SkeletonItem() {
  return (
    <div className="border-b border-white/[0.04] py-3 px-5">
      <div className="space-y-2">
        <div className="h-4 bg-white/[0.06] animate-pulse rounded w-3/4" />
        <div className="h-3 bg-white/[0.04] animate-pulse rounded w-1/2" />
      </div>
    </div>
  );
}

export function MetricDetailSheet({
  metricType,
  open,
  onOpenChange,
  interval,
  page,
  scope,
  scopeId,
  timeZone,
  startDate,
  endDate,
}: MetricDetailSheetProps) {
  const [currentMetricType, setCurrentMetricType] = useState(metricType);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (metricType !== currentMetricType) {
      setIsTransitioning(true);
      setCurrentMetricType(metricType);
      const timer = setTimeout(() => setIsTransitioning(false), 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [metricType, currentMetricType]);

  const { data, isLoading, error, refetch } = useActivityDetails({
    metricType,
    interval,
    page,
    scope,
    ...(scopeId !== undefined && { scopeId }),
    timeZone,
    ...(startDate !== undefined && { startDate }),
    ...(endDate !== undefined && { endDate }),
    enabled: open,
  });

  const title = METRIC_TITLES[metricType];
  const color = METRIC_COLORS[metricType];
  const emptyConfig = EMPTY_MESSAGES[metricType];
  const EmptyIcon = emptyConfig.icon;
  const itemCount = data?.data?.length ?? 0;
  const showLoading = isLoading || isTransitioning;

  if (!open) return null;

  return (
    <OverlayPanel
      open={open}
      onClose={() => onOpenChange(false)}
      title={title}
      titleClassName={color}
      badge={itemCount > 0 ? (
        <span className="text-xs px-2 py-0.5 rounded bg-white/[0.06] text-muted-foreground">
          {itemCount}
        </span>
      ) : undefined}
    >
      <div className="flex-1 overflow-y-auto">
        {showLoading && (
          <div className="space-y-0">
            {Array.from({ length: 5 }).map((_, idx) => (
              <SkeletonItem key={idx} />
            ))}
          </div>
        )}

        {error && !showLoading && (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-muted-foreground text-sm mb-4">
              Failed to load {metricType}. Please try again.
            </p>
            <Button onClick={() => { void refetch(); }} variant="outline" size="sm">
              Try again
            </Button>
          </div>
        )}

        {!showLoading && !error && data?.data.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <EmptyIcon className="h-10 w-10 text-white/[0.15] mb-3" />
            <p className="text-muted-foreground text-xs">{emptyConfig.message}</p>
          </div>
        )}

        {!showLoading && !error && data?.data && data.data.length > 0 && (
          <div>
            {metricType === 'features' && (
              <FeatureDetailList items={data.data as FeatureDetail[]} />
            )}
            {metricType === 'tasks' && (
              <TaskDetailList items={data.data as TaskDetail[]} />
            )}
            {metricType === 'decisions' && (
              <DecisionDetailList items={data.data as DecisionDetail[]} />
            )}
            {metricType === 'sessions' && (
              <SessionDetailList items={data.data as SessionDetail[]} />
            )}
            {metricType === 'epicRequests' && (
              <EpicRequestDetailList items={data.data as EpicRequestDetail[]} />
            )}
            {metricType === 'epics' && (
              <EpicDetailList items={data.data as EpicDetail[]} />
            )}
            {metricType === 'implementationTime' && (
              <ImplementationTimeDetailList items={data.data as ImplementationTimeDetail[]} />
            )}
          </div>
        )}
      </div>
    </OverlayPanel>
  );
}
